import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Button, TextField, Chip, Tooltip,
  IconButton, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, CircularProgress, Alert, Snackbar, Card,
  CardContent, Avatar, LinearProgress, Menu, MenuItem, InputAdornment,
  Select, FormControl, InputLabel, Badge
} from '@mui/material';
import {
  Search, FilterList, Refresh, LocalShipping, CreditCard,
  MoreVert, Visibility, Edit, Cancel, CheckCircle, Close,
  PersonOutline, Receipt, Print, History, MonetizationOn,
  Email, AttachMoney, ContentCopy, Phone, Schedule, 
  Send, Download, ShoppingCart
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { blue, green, red, orange, grey } from '@mui/material/colors';
import pb from './pocketbase';

// Styled components
const StatusChip = styled(Chip)(({ status, theme }) => ({
  fontWeight: 600,
  ...(status === 'hold' && {
    backgroundColor: orange[100],
    color: orange[800],
  }),
  ...(status === 'processing' && {
    backgroundColor: blue[100],
    color: blue[800],
  }),
  ...(status === 'shipped' && {
    backgroundColor: blue[500],
    color: blue[50],
  }),
  ...(status === 'delivered' && {
    backgroundColor: green[100],
    color: green[800],
  }),
  ...(status === 'cancelled' && {
    backgroundColor: red[100],
    color: red[800],
  }),
}));

const PaymentStatusChip = styled(Chip)(({ status, theme }) => ({
  fontWeight: 600,
  ...(status === 'paid' && {
    backgroundColor: green[100],
    color: green[800],
  }),
  ...(status === 'pending' && {
    backgroundColor: orange[100],
    color: orange[800],
  }),
  ...(status === 'error' && {
    backgroundColor: red[100],
    color: red[800],
  }),
  ...(status === 'unpaid' && {
    backgroundColor: grey[300],
    color: grey[800],
  }),
}));

const ListItemIcon = styled(Box)(({ theme }) => ({
  minWidth: 36,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const Orders = () => {
  // Current user and time
  const currentDateTime = '2025-04-03 03:27:36';
  const currentUser = 'MdAbuNafisNiloy';

  // State management
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalOrders, setTotalOrders] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [updateStatusModalOpen, setUpdateStatusModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusComment, setStatusComment] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [paymentStatusModalOpen, setPaymentStatusModalOpen] = useState(false);
  const [newPaymentStatus, setNewPaymentStatus] = useState('');
  const [statistics, setStatistics] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    pendingOrders: 0
  });

  // Fetch orders from PocketBase
  const fetchOrders = async () => {
    try {
      setLoading(true);
      
      // Build filter string
      let filterString = '';
      if (searchQuery) {
        filterString = `id~'${searchQuery}' || email~'${searchQuery}'`;
      }
      
      if (filterStatus !== 'all') {
        if (filterString) filterString += ' && ';
        filterString += `order_status='${filterStatus}'`;
      }
      
      // Fetch orders with pagination
      const resultList = await pb.collection('orders').getList(page + 1, rowsPerPage, {
        filter: filterString,
        expand: 'user_id,seller_id,invoice',
        sort: '-created'
      });
      
        // Get all unique user IDs and seller IDs from the orders
        const userIds = [...new Set(resultList.items.filter(order => order.user_id).map(order => order.user_id))];
        const sellerIds = [...new Set(resultList.items.filter(order => order.seller_id).map(order => order.seller_id))];
        
        // Create a map to hold user data and seller data
        const userData = {};
        const sellerData = {};
        
        // Fetch all users at once if there are any user IDs
        if (userIds.length > 0) {
          try {
            const users = await pb.collection('users').getFullList({
                filter: `id="${userIds[0]}" ${userIds.slice(1).map(id => `|| id="${id}"`).join(' ')}`,
                fields: 'id,name,email,phone'
            });
            
            // Populate the userData map
            users.forEach(user => {
              userData[user.id] = user;
            });
          } catch (err) {
            console.error('Error fetching users:', err);
          }
        }
        
        // Fetch all sellers at once if there are any seller IDs
        if (sellerIds.length > 0) {
          try {
            const sellers = await pb.collection('sellers').getFullList({
              filter:  `id="${sellerIds[0]}" ${sellerIds.slice(1).map(id => `|| id="${id}"`).join(' ')}`,
              fields: 'id,name,email,phone'
            });
            
            // Populate the sellerData map
            sellers.forEach(seller => {
              sellerData[seller.id] = seller;
            });
          } catch (err) {
            console.error('Error fetching sellers:', err);
          }
        }
        
        // Now manually add the expanded data to each order
        const ordersWithExpand = resultList.items.map(order => {
          // Create the expand property if it doesn't exist
          if (!order.expand) {
            order.expand = {};
          }
          
          // Add user data if available
          if (order.user_id && userData[order.user_id]) {
            order.expand.user_id = userData[order.user_id];
          }
          
          // Add seller data if available
          if (order.seller_id && sellerData[order.seller_id]) {
            order.expand.seller_id = sellerData[order.seller_id];
          }
          
          return order;
        });
        
        // Set current date and user in state for future use
        const currentDateTime = "2025-04-03 04:14:31";
        const currentUser = "MdAbuNafisNiloy";
        
        // Update state with orders and total count
        setOrders(ordersWithExpand);
        setTotalOrders(resultList.totalItems);
      
         

      // Fetch statistics
      fetchOrderStatistics();
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders. Please try again later.');
      setLoading(false);
    }
  };

  // Fetch order statistics
  const fetchOrderStatistics = async () => {
    try {
      // Total orders
      const totalOrders = await pb.collection('orders').getFullList({
        fields: 'id'
      });
      
      // Pending orders
      const pendingOrders = await pb.collection('orders').getFullList({
        filter: "order_status='hold' || order_status='processing'",
        fields: 'id'
      });
      
      // Total revenue
      const revenue = await pb.collection('orders').getFullList({
        filter: "payment_status='paid'",
        fields: 'product_price'
      });
      
      const totalRevenue = revenue.reduce((sum, order) => sum + (order.product_price || 0), 0);
      
      setStatistics({
        totalOrders: totalOrders.length,
        totalRevenue,
        pendingOrders: pendingOrders.length
      });
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [page, rowsPerPage, searchQuery, filterStatus]);

  // Handle view order details
  const handleViewOrder = async (orderId) => {
    try {
      // Fetch the basic order first
      const order = await pb.collection('orders').getOne(orderId, {
        expand: "products"
        });
      
      // Initialize expand object if it doesn't exist
      if (!order.expand) {
        order.expand = {};
      }
      
      // Create an array of promises for parallel fetching
      const fetchPromises = [];
      
      // Fetch user data if user_id exists
      if (order.user_id) {
        const userPromise = pb.collection('users').getOne(order.user_id)
          .then(user => { order.expand.user_id = user; })
          .catch(err => { console.error('Error fetching user:', err); });
        fetchPromises.push(userPromise);
      }
      
      // Fetch seller data if seller_id exists
      if (order.seller_id) {
        const sellerPromise = pb.collection('sellers').getOne(order.seller_id)
          .then(seller => { order.expand.seller_id = seller; })
          .catch(err => { console.error('Error fetching seller:', err); });
        fetchPromises.push(sellerPromise);
      }
      
      // Fetch invoice data if invoice exists
      if (order.invoice) {
        const invoicePromise = pb.collection('invoice').getOne(order.invoice)
          .then(invoice => { order.expand.invoice = invoice; })
          .catch(err => { console.error('Error fetching invoice:', err); });
        fetchPromises.push(invoicePromise);
      }
      
      // Fetch product data if product_id exists
      
      
      // Wait for all fetch operations to complete
      await Promise.all(fetchPromises);
      
      // Add timestamps for reference
      order.currentDateTime = "2025-04-03 04:30:16";
      order.currentUser = "MdAbuNafisNiloy";
      
      setSelectedOrder(order);
      setViewModalOpen(true);
    } catch (err) {
      console.error('Error fetching order details:', err);
      setSnackbar({
        open: true,
        message: 'Failed to load order details',
        severity: 'error'
      });
    }
  };

  // Handle order status update
  const updateOrderStatus = async () => {
    try {
      setIsUpdatingStatus(true);
      
      // Get current order
      const order = await pb.collection('orders').getOne(currentOrderId, {
        expand: 'seller_id,invoice'
      });
      
      // Update order status
      const data = {
        order_status: newStatus,
      };
      
      // If cancelling an order and payment is pending, update payment status
      if (newStatus === 'cancelled' && order.payment_status === 'pending') {
        data.payment_status = 'error';
      }
      
      await pb.collection('orders').update(currentOrderId, data);
      
      // Handle cancellation - adjust seller balances
      if (newStatus === 'cancelled' && order.seller_id ) {
        // Get seller
        const seller = await pb.collection('sellers').getOne(order.seller_id);
        
        // Update seller stats
        await pb.collection('sellers').update(seller.id, {
          total_orders: (seller.total_orders || 0) - 1,
          total_sale: (seller.total_sale || 0) - (order.product_price || 0),
          total_commission: (seller.total_commission || 0) - (order.commission || 0),
        //   total_balance: (seller.total_balance || 0) - ((order.product_price || 0) - (order.commission || 0))
          total_balance: order.payment_method !== 'Cash on Delivery' ? (seller.total_balance || 0) - (order.product_price || 0)   : (seller.total_balance || 0)
        });
      }
      
      // Update invoice if exists
      if (order.invoice) {
        await pb.collection('invoice').update(order.invoice, {
          status: newStatus === 'cancelled' ? 'error' : order.payment_status
        });
      }
      
      setSnackbar({
        open: true,
        message: `Order status updated to ${newStatus}`,
        severity: 'success'
      });
      
      fetchOrders();
      setUpdateStatusModalOpen(false);
    } catch (err) {
      console.error('Error updating order status:', err);
      setSnackbar({
        open: true,
        message: 'Failed to update order status',
        severity: 'error'
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Update payment status
  const updatePaymentStatus = async () => {
    try {
      setIsUpdatingStatus(true);
      
      // Get current order with product details
      const order = await pb.collection('orders').getOne(currentOrderId, {
        expand: 'products'
      });
      
      // Determine if this is a digital product
      let isDigitalProduct = false;
      if (order.expand && order.expand.products) {
        isDigitalProduct = order.expand.products.digital_product === true;
      }
      
      // Prepare update data
      const updateData = {
        payment_status: newPaymentStatus
      };
      
      // If this is a digital product and payment status is being set to 'paid',
      // automatically set delivery status to 'delivered'
      if (isDigitalProduct && newPaymentStatus === 'paid') {
        updateData.order_status = 'delivered';
        console.log(`[${currentDateTime}] User ${currentUser} automatically marked digital product order ${currentOrderId} as delivered after payment confirmation`);
      }
      
      // Update order with payment status (and possibly order status)
      await pb.collection('orders').update(currentOrderId, updateData);
      
      // Update invoice if exists
      if (order.invoice) {
        await pb.collection('invoice').update(order.invoice, {
          status: newPaymentStatus
        });
      }
      
      // If payment is now 'paid' and wasn't before, update seller balance
      if (newPaymentStatus === 'paid' && order.payment_status !== 'paid' && order.seller_id) {
        const seller = await pb.collection('sellers').getOne(order.seller_id);
        
        await pb.collection('sellers').update(seller.id, {
          total_sale: (seller.total_sale || 0) + (order.product_price || 0),
          total_commission: (seller.total_commission || 0) + (order.commission || 0),
          total_balance: (seller.total_balance || 0) + ((order.product_price || 0) - (order.commission || 0))
        });
      }
      
      // If payment was 'paid' but now isn't, reverse the seller balance
      if (order.payment_status === 'paid' && newPaymentStatus !== 'paid' && order.seller_id) {
        const seller = await pb.collection('sellers').getOne(order.seller_id);
        
        await pb.collection('sellers').update(seller.id, {
          total_sale: (seller.total_sale || 0) - (order.product_price || 0),
          total_commission: (seller.total_commission || 0) - (order.commission || 0),
          total_balance: (seller.total_balance || 0) - ((order.product_price || 0) - (order.commission || 0))
        });
      }
      
      // Customize success message based on whether it's a digital product that was also marked as delivered
      const successMessage = isDigitalProduct && newPaymentStatus === 'paid' 
        ? `Payment status updated to ${newPaymentStatus} and digital product automatically marked as delivered`
        : `Payment status updated to ${newPaymentStatus}`;
      
      setSnackbar({
        open: true,
        message: successMessage,
        severity: 'success'
      });
      
      fetchOrders();
      setPaymentStatusModalOpen(false);
    } catch (err) {
      console.error('Error updating payment status:', err);
      setSnackbar({
        open: true,
        message: 'Failed to update payment status',
        severity: 'error'
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'BDT',
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  
  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Orders Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage all customer orders
          </Typography>
        </Box>
        
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: blue[500], mr: 2 }}>
                <ShoppingCart />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  {statistics.totalOrders}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Orders
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: orange[500], mr: 2 }}>
                <Schedule />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  {statistics.pendingOrders}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Orders
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: green[500], mr: 2 }}>
                <AttachMoney />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  {formatCurrency(statistics.totalRevenue)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Revenue
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter */}
      <Paper sx={{ mb: 3, p: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by ID or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                size="small"
              >
                Filter
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => {
                  setFilterStatus('all');
                  setSearchQuery('');
                  fetchOrders();
                }}
                size="small"
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
      >
        <MenuItem 
          selected={filterStatus === 'all'} 
          onClick={() => {
            setFilterStatus('all');
            setFilterAnchorEl(null);
          }}
        >
          All Orders
        </MenuItem>
        <MenuItem 
          selected={filterStatus === 'hold'} 
          onClick={() => {
            setFilterStatus('hold');
            setFilterAnchorEl(null);
          }}
        >
          Hold
        </MenuItem>
        <MenuItem 
          selected={filterStatus === 'processing'} 
          onClick={() => {
            setFilterStatus('processing');
            setFilterAnchorEl(null);
          }}
        >
          Processing
        </MenuItem>
        <MenuItem 
          selected={filterStatus === 'shipped'} 
          onClick={() => {
            setFilterStatus('shipped');
            setFilterAnchorEl(null);
          }}
        >
          Shipped
        </MenuItem>
        <MenuItem 
          selected={filterStatus === 'delivered'} 
          onClick={() => {
            setFilterStatus('delivered');
            setFilterAnchorEl(null);
          }}
        >
          Delivered
        </MenuItem>
      </Menu>

      {/* Orders Table */}
      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading orders...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <Typography variant="body1">
                        No orders found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Try adjusting your search or filter to find what you're looking for
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((order) => (
                    <TableRow 
                      key={order.id} 
                      hover
                      onClick={() => handleViewOrder(order.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{order.id.slice(0, 8)}...</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: blue[500] }}>
                            {order.expand?.user_id?.name?.charAt(0) || 'C'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {order.expand?.user_id?.name || 'Customer'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {order.expand?.user_id?.email || 'No email'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(order.created)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(order.created)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(order.product_price)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          size="small"
                          status={order.order_status}
                          label={order.order_status}
                        />
                      </TableCell>
                      <TableCell>
                        <PaymentStatusChip
                          size="small"
                          status={order.payment_status}
                          label={order.payment_status}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewOrder(order.id);
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="More Actions">
                            <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCurrentOrderId(order.id);
                                setActionMenuAnchorEl(e.currentTarget);
                              }}
                            >
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalOrders}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      )}

      {/* View Order Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        fullWidth
        maxWidth="md"
      >
        {selectedOrder && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={600}>
                  Order Details
                </Typography>
                <IconButton
                  edge="end"
                  onClick={() => setViewModalOpen(false)}
                >
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Order Information
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Order ID:
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {selectedOrder.id}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Date:
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDate(selectedOrder.created)} {formatTime(selectedOrder.created)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Status:
                      </Typography>
                      <StatusChip
                        size="small"
                        status={selectedOrder.order_status}
                        label={selectedOrder.order_status}
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Payment Status:
                      </Typography>
                      <PaymentStatusChip
                        size="small"
                        status={selectedOrder.payment_status}
                        label={selectedOrder.payment_status}
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Payment Method:
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {selectedOrder.payment_method || 'N/A'}
                      </Typography>
                    </Box>
                    
                    {selectedOrder.notes && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Notes:
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 1, bgcolor: 'background.default' }}>
                          <Typography variant="body2">{selectedOrder.notes}</Typography>
                        </Paper>
                      </Box>
                    )}
                  </Paper>
                  
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Payment Details
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Product Price:
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatCurrency(selectedOrder.product_price)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Delivery Charge:
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatCurrency(selectedOrder.delivery_charge)}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Total:
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {formatCurrency((selectedOrder.product_price || 0) + (selectedOrder.delivery_charge || 0))}
                      </Typography>
                    </Box>
                    
                   
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Customer Information
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Avatar sx={{ width: 56, height: 56, bgcolor: blue[500] }}>
                        {selectedOrder.expand?.user_id?.name?.charAt(0) || 'C'}
                      </Avatar>
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          {selectedOrder.expand?.user_id?.name || 'Customer'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedOrder.expand?.user_id?.email || 'No email'}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Button
                      variant="outlined"
                      startIcon={<Email />}
                      size="small"
                      sx={{ mr: 1 }}
                      disabled={!selectedOrder.expand?.user_id?.email}
                      href={`mailto:${selectedOrder.expand?.user_id?.email}`}
                    >
                      Contact Customer
                    </Button>
                  </Paper>
                  
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Seller Information
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Seller:
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {selectedOrder.expand?.seller_id?.shop_name || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedOrder.expand?.seller_id?.email || 'No email'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Commission:
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {formatCurrency(selectedOrder.commission)}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Products
                    </Typography>
                    
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell>Variants</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedOrder.expand?.products ? (
                            selectedOrder.expand.products.map((product, index) => {
                              // Get variant information from any of the three possible formats
                              let productVariant = null;
                              
                              // Format 1: variants object with product IDs as keys
                              if (selectedOrder.variants && typeof selectedOrder.variants === 'object' && !Array.isArray(selectedOrder.variants)) {
                                productVariant = selectedOrder.variants[product.id];
                              }
                              
                              // Format 2: variant info in products_id array
                              if (!productVariant && selectedOrder.products_id) {
                                const productInfo = Array.isArray(selectedOrder.products_id) 
                                  ? selectedOrder.products_id.find(p => p.id === product.id)
                                  : null;
                                if (productInfo?.variant) {
                                  productVariant = productInfo.variant;
                                }
                              }
                              
                              // Format 3: variants array with product details
                              if (!productVariant && Array.isArray(selectedOrder.variants)) {
                                const variantInfo = selectedOrder.variants.find(v => v.product_id === product.id);
                                if (variantInfo) {
                                  productVariant = {
                                    price: variantInfo.price,
                                    // Map other fields if needed
                                  };
                                }
                              }

                              return (
                                <TableRow key={product.id}>
                                  <TableCell>{product.title || 'Product'}</TableCell>
                                  <TableCell>
                                    {productVariant ? (
                                      <Box>
                                        {productVariant.color && (
                                          <Typography variant="body2" color="text.secondary">
                                            Color: {productVariant.color}
                                          </Typography>
                                        )}
                                        {productVariant.size && (
                                          <Typography variant="body2" color="text.secondary">
                                            Size: {productVariant.size}
                                          </Typography>
                                        )}
                                        {productVariant.weight && (
                                          <Typography variant="body2" color="text.secondary">
                                            Weight: {productVariant.weight}
                                          </Typography>
                                        )}
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        No variant information
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="right">{formatCurrency(product.price)}</TableCell>
                                  <TableCell align="right">
                                    {selectedOrder.products_id?.find(p => p.id === product.id)?.quantity || 1}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency((selectedOrder.products_id?.find(p => p.id === product.id)?.quantity || 1) * product.price)}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          ) : (
                            <TableRow>
                              <TableCell colSpan={5} align="center">
                                No product details available
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                onClick={() => setViewModalOpen(false)}
                variant="outlined"
              >
                Close
              </Button>
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={() => {
                  setCurrentOrderId(selectedOrder.id);
                  setNewStatus(selectedOrder.order_status);
                  setUpdateStatusModalOpen(true);
                  setViewModalOpen(false);
                }}
              >
                Update Status
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<CreditCard />}
                onClick={() => {
                  setCurrentOrderId(selectedOrder.id);
                  setNewPaymentStatus(selectedOrder.payment_status);
                  setPaymentStatusModalOpen(true);
                  setViewModalOpen(false);
                }}
              >
                Update Payment
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={() => setActionMenuAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          handleViewOrder(currentOrderId);
          setActionMenuAnchorEl(null);
        }}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          View Details
        </MenuItem>
        
        <MenuItem onClick={() => {
          setNewStatus('processing');
          setUpdateStatusModalOpen(true);
          setActionMenuAnchorEl(null);
        }}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          Update Status
        </MenuItem>
        
        <MenuItem onClick={() => {
          setNewPaymentStatus('paid');
          setPaymentStatusModalOpen(true);
          setActionMenuAnchorEl(null);
        }}>
          <ListItemIcon>
            <CreditCard fontSize="small" />
          </ListItemIcon>
          Update Payment
        </MenuItem>
        
        
      </Menu>

      {/* Status Update Modal */}
      <Dialog 
        open={updateStatusModalOpen} 
        onClose={() => !isUpdatingStatus && setUpdateStatusModalOpen(false)}
      >
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              label="Status"
            >
              <MenuItem value="hold">Hold</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
              <MenuItem value="shipped">Shipped</MenuItem>
              <MenuItem value="delivered">Delivered</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            label="Status Comment"
            multiline
            rows={3}
            value={statusComment}
            onChange={(e) => setStatusComment(e.target.value)}
            sx={{ mt: 2 }}
          />
          
          {newStatus === 'cancelled' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Cancelling an order will update financial records and may affect seller balance.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setUpdateStatusModalOpen(false)} 
            disabled={isUpdatingStatus}
          >
            Cancel
          </Button>
          <Button
            onClick={updateOrderStatus}
            color={newStatus === 'cancelled' ? 'error' : 'primary'}
            variant="contained"
            disabled={isUpdatingStatus}
          >
            {isUpdatingStatus ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Status Update Modal */}
      <Dialog 
        open={paymentStatusModalOpen} 
        onClose={() => !isUpdatingStatus && setPaymentStatusModalOpen(false)}
      >
        <DialogTitle>Update Payment Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Payment Status</InputLabel>
            <Select
              value={newPaymentStatus}
              onChange={(e) => setNewPaymentStatus(e.target.value)}
              label="Payment Status"
            >
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="unpaid">Unpaid</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="error">Error</MenuItem>
            </Select>
          </FormControl>
          
          {(newPaymentStatus === 'paid' || newPaymentStatus === 'error') && (
            <Alert severity={newPaymentStatus === 'paid' ? 'info' : 'warning'} sx={{ mt: 2 }}>
              {newPaymentStatus === 'paid' 
                ? 'Marking as paid will update seller balance and commission records. For digital products, the order status will automatically change to "delivered".'
                : 'Marking as error may affect seller balance if the order was previously paid.'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setPaymentStatusModalOpen(false)} 
            disabled={isUpdatingStatus}
          >
            Cancel
          </Button>
          <Button
            onClick={updatePaymentStatus}
            color={newPaymentStatus === 'paid' ? 'success' : 'primary'}
            variant="contained"
            disabled={isUpdatingStatus}
          >
            {isUpdatingStatus ? 'Updating...' : 'Update Payment Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Orders;